// Feb 23 2026 · Owner "Enter Hotel" inspection banner.
// Sticky top banner that renders whenever the Owner is viewing another
// hotel's dashboards via the `X-Owner-View-As-Hotel-Id` header path.
// Provides an explicit "Exit" affordance so the Owner can leave the
// inspected hotel and return to the platform Owner dashboard.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, X } from 'lucide-react';
import {
  OWNER_INSPECT_HOTEL_ID_KEY,
  OWNER_INSPECT_HOTEL_NAME_KEY,
} from '../../lib/axiosSetup';

const readInspect = () => {
  try {
    return {
      id: sessionStorage.getItem(OWNER_INSPECT_HOTEL_ID_KEY) || '',
      name: sessionStorage.getItem(OWNER_INSPECT_HOTEL_NAME_KEY) || '',
    };
  } catch (_) {
    return { id: '', name: '' };
  }
};

export const enterHotelAsOwner = ({ id, name }) => {
  if (!id) return;
  try {
    sessionStorage.setItem(OWNER_INSPECT_HOTEL_ID_KEY, id);
    sessionStorage.setItem(OWNER_INSPECT_HOTEL_NAME_KEY, name || id);
  } catch (_) {
    /* ignore */
  }
};

export const exitOwnerInspect = () => {
  try {
    sessionStorage.removeItem(OWNER_INSPECT_HOTEL_ID_KEY);
    sessionStorage.removeItem(OWNER_INSPECT_HOTEL_NAME_KEY);
  } catch (_) {
    /* ignore */
  }
};

export default function OwnerInspectBanner() {
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(readInspect);

  useEffect(() => {
    // Poll session-storage on each navigation change so the banner
    // stays in sync when the user enters or exits inspection mode.
    const onStorage = () => setCtx(readInspect());
    window.addEventListener('storage', onStorage);
    const interval = setInterval(onStorage, 800);
    return () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(interval);
    };
  }, []);

  const handleExit = useCallback(() => {
    exitOwnerInspect();
    setCtx({ id: '', name: '' });
    // Hard reload to /owner so all cached hotel-scoped state is dropped.
    window.location.href = '/owner';
  }, []);

  if (!ctx.id) return null;

  return (
    <div
      className="sticky top-0 z-[60] flex w-full items-center justify-between gap-3 border-b border-amber-500/40 bg-amber-500/95 px-4 py-2 text-slate-900 shadow-sm"
      data-testid="owner-inspect-banner"
    >
      <div className="flex min-w-0 items-center gap-2 text-sm">
        <Eye className="h-4 w-4 shrink-0" />
        <span className="font-semibold uppercase tracking-wide">Owner inspection</span>
        <span className="hidden sm:inline">·</span>
        <span className="truncate" data-testid="owner-inspect-hotel-name">{ctx.name || ctx.id}</span>
        <span className="hidden font-mono text-[11px] text-slate-800/70 md:inline">({ctx.id})</span>
      </div>
      <button
        onClick={handleExit}
        className="flex items-center gap-1 rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-800"
        data-testid="owner-inspect-exit"
        aria-label="Exit Owner inspection"
      >
        <X className="h-3.5 w-3.5" />
        Exit
      </button>
    </div>
  );
}
